import { Card, Collapse, CollapseProps, Skeleton, Space, theme } from 'antd';
import { useRouter } from 'next/router';
import { PropsWithChildren, ReactElement, cloneElement, useEffect, useMemo } from 'react';
import { Controller, ControllerProps, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateTicketFormValues, EditTicketFormValues } from '../../../../../types/form';
import CustomField, { CustomFieldProps } from '../../../../components/CustomField';
import { customFieldTypeMap } from '../../../../components/CustomField/CustomField.type';
import { baseAgentFormSystemFieldKeyList } from '../../../../constants/forms/ticketFormFields';
import useAgentFormById, { ListenedConditionOnForm } from '../../../../hooks/ticketDetailPage/useAgentFormById';
import useCurrentTicketStatus from '../../../../hooks/ticketDetailPage/useCurrentTicketStatus';
import useFieldDefinitionListFromAgentForm from '../../../../hooks/ticketDetailPage/useFieldDefinitionListFromAgentForm';
import {
    Attributes,
    FieldCondition,
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    statusOptions
} from '../../../../model/types';
import { getFieldRuleByKey } from '../../../../utils/forms/formHelper.util';
import { getValidationRules } from '../../../../utils/forms/formHelper.validators';
import { defineMode } from '../../../../utils/forms/formHelpers';
import { nullishValue } from '../../../../utils/helpers';

const MIN_FIELD_ARRAY_LENGTH = baseAgentFormSystemFieldKeyList.length;

const FORM_FIELD_KEY = `${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKey.formId.replace(
    '.',
    '$'
)}` as `fields.${string}`;

const generateReactHookFormFriendlyFieldName = (fieldKey: string): any =>
    `${TICKET_FORM_FIELDS_KEY}.${fieldKey.replace(new RegExp('\\.', 'gi'), '$')}`;
const generateReactHookFormFriendlyFieldNameWithoutFields = (fieldKey: string): any =>
    `${fieldKey.replace(new RegExp('\\.', 'gi'), '$')}`;
const shouldRenderForOptimisticUI = (fieldDefinitionKey: string) =>
    [TicketSystemFieldsKey.assignee, TicketSystemFieldsKey.requester].includes(
        fieldDefinitionKey as TicketSystemFieldsKey
    );

/**
 * The `ConditionController` component triggers validation action for specified fields when the watched field
 * value changes.
 * @param  - `conditions`: An array of `FieldCondition` objects. Each `FieldCondition` object has an `action` property which represents the fieldKey to trigger validation on
 * @param  - `fieldKey`: a fieldKey to watch for value changes
 */
function ConditionController({ conditions, fieldKey }: { conditions: FieldCondition[]; fieldKey: string }) {
    const watched = useWatch({ name: generateReactHookFormFriendlyFieldName(fieldKey) });
    const {
        trigger,
        formState: { isDirty }
    } = useFormContext();
    useEffect(() => {
        conditions?.forEach(({ action }) => {
            isDirty && trigger(generateReactHookFormFriendlyFieldName(action.fieldKey));
        });
    }, [conditions, isDirty, watched, trigger]);
    return null;
}

/**
 * The `ListenedConditionController` function takes in a condition and a React element as props,
 * and updates the `fieldRequirement` prop of the element based on the condition.
 * @param  - `listenedConditionOnForm`: A ListenedConditionOnForm object that represents only the last-listened condition on the form.
 */

function ListenedConditionController<P extends CustomFieldProps>({
    listenedConditionOnForm,
    children
}: PropsWithChildren<{
    listenedConditionOnForm: ListenedConditionOnForm;
    children: ReactElement<P>;
}>) {
    const watched = useWatch({
        name: listenedConditionOnForm?.conditionFieldKey
            ? generateReactHookFormFriendlyFieldName(listenedConditionOnForm.conditionFieldKey)
            : ''
    });

    if (!listenedConditionOnForm) return children;

    if (!listenedConditionOnForm.value.includes(watched)) return null;

    return cloneElement<P>(children, {
        ...children.props,
        containerProps: {
            ...children.props.containerProps,
            ...(listenedConditionOnForm.value.includes(watched)
                ? { fieldRequirement: listenedConditionOnForm.action.required }
                : { fieldRequirement: children.props.containerProps.fieldRequirement })
        }
    });
}

function ConditionApplier<P extends ControllerProps>({
    children,
    listenedConditionOnForm
}: PropsWithChildren<{
    children: ReactElement<P>;
    listenedConditionOnForm: ListenedConditionOnForm;
}>) {
    const watched = useWatch({
        name: listenedConditionOnForm?.conditionFieldKey
            ? generateReactHookFormFriendlyFieldName(listenedConditionOnForm.conditionFieldKey)
            : ''
    });
    if (!listenedConditionOnForm) return children;

    if (listenedConditionOnForm.value.includes(watched)) return children;

    // no validation for fieldRuleOnForm
    return cloneElement<P>(children, {
        ...children.props,
        rules: {
            validate: () => true
        }
    });
}

export default function AgentFormActiveFieldsContainer() {
    const { asPath } = useRouter();
    const {
        t,
        i18n: { language }
    } = useTranslation('tickets');
    const {
        token: { padding, borderRadius, paddingXS }
    } = theme.useToken();

    const formId = useWatch<CreateTicketFormValues | EditTicketFormValues, typeof FORM_FIELD_KEY>({
        name: FORM_FIELD_KEY
    });

    const formMode = defineMode(asPath);
    const isEditMode = formMode === 'edit';
    const ticketStatus = useCurrentTicketStatus();

    const currentAgentForm = useAgentFormById(Number(formId));
    const allFields = useFieldDefinitionListFromAgentForm(Number(formId));
    const ticketFields = useMemo(() => allFields.filter((field) => !field.gsocialField), [allFields]);
    const gsocialFields = useMemo(() => allFields.filter((field) => field.gsocialField), [allFields]);
    const isTicketClosed = ticketStatus === statusOptions.Closed;

    const items: CollapseProps['items'] = useMemo(
        () => [
            {
                key: '1',
                label: t('gsocialFieldsTitle'),
                children: (
                    <Space direction="vertical" className="full-width" size="small">
                        {gsocialFields.map((fieldDefinition) => {
                            if (!fieldDefinition) return null;

                            const { key, type } = fieldDefinition;

                            const fieldRuleOnForm = currentAgentForm?.fields?.find(getFieldRuleByKey(fieldDefinition));

                            return (
                                <ConditionApplier
                                    key={fieldDefinition.key}
                                    listenedConditionOnForm={fieldRuleOnForm?.listenedConditionOnForm}
                                >
                                    <Controller<CreateTicketFormValues | EditTicketFormValues>
                                        name={generateReactHookFormFriendlyFieldName(key)}
                                        render={({
                                            field: { value, onChange, onBlur, ref },
                                            fieldState: { error },
                                            formState: { defaultValues }
                                        }) => {
                                            return (
                                                <>
                                                    {fieldRuleOnForm?.conditions && (
                                                        <ConditionController
                                                            fieldKey={key}
                                                            conditions={fieldRuleOnForm.conditions}
                                                        />
                                                    )}
                                                    <ListenedConditionController
                                                        listenedConditionOnForm={
                                                            fieldRuleOnForm?.listenedConditionOnForm
                                                        }
                                                    >
                                                        <CustomField
                                                            {...(type === customFieldTypeMap.CHECKBOX
                                                                ? {
                                                                      checkboxDefaultValue: defaultValues['fields'][
                                                                          generateReactHookFormFriendlyFieldNameWithoutFields(
                                                                              key
                                                                          )
                                                                      ] as string
                                                                  }
                                                                : {})}
                                                            containerProps={{
                                                                errorMessage: error?.message,
                                                                fieldRequirement:
                                                                    fieldRuleOnForm?.required ||
                                                                    fieldDefinition?.required
                                                            }}
                                                            disabled={isTicketClosed}
                                                            fieldDefinition={fieldDefinition}
                                                            value={value}
                                                            onBlur={onBlur}
                                                            onChange={onChange}
                                                            antdProps={{ ref }}
                                                        />
                                                    </ListenedConditionController>
                                                </>
                                            );
                                        }}
                                        rules={{
                                            validate: getValidationRules({
                                                fieldRuleOnForm,
                                                fieldDefinition
                                            })
                                        }}
                                    />
                                </ConditionApplier>
                            );
                        })}
                    </Space>
                )
            }
        ],
        [currentAgentForm?.fields, gsocialFields, isTicketClosed, t]
    );
    if (
        nullishValue(formId) || allFields.length < MIN_FIELD_ARRAY_LENGTH || isEditMode
            ? nullishValue(ticketStatus)
            : false
    )
        return (
            <Space direction="vertical" className="full-width" size="large">
                <Skeleton.Input active className="full-width" />
                <Skeleton.Input active className="full-width" />
                <Skeleton.Input active className="full-width" />
            </Space>
        );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: paddingXS }} key={language}>
            <Card
                bodyStyle={{ padding: padding }}
                style={{
                    borderRadius: borderRadius,
                    border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
            >
                <Space direction="vertical" className="full-width" size="small">
                    {ticketFields.map((fieldDefinition) => {
                        if (!fieldDefinition) return null;

                        const { attributes, key, type } = fieldDefinition;
                        if (attributes.includes(Attributes.FIXED_PLACE_IN_UI) && !shouldRenderForOptimisticUI(key))
                            return null;

                        const fieldRuleOnForm = currentAgentForm?.fields?.find(getFieldRuleByKey(fieldDefinition));

                        return (
                            <ConditionApplier
                                key={fieldDefinition.key}
                                listenedConditionOnForm={fieldRuleOnForm?.listenedConditionOnForm}
                            >
                                <Controller<CreateTicketFormValues | EditTicketFormValues>
                                    name={generateReactHookFormFriendlyFieldName(key)}
                                    render={({
                                        field: { value, onChange, onBlur, ref },
                                        fieldState: { error },
                                        formState: { defaultValues }
                                    }) => {
                                        return (
                                            <>
                                                {fieldRuleOnForm?.conditions && (
                                                    <ConditionController
                                                        fieldKey={key}
                                                        conditions={fieldRuleOnForm.conditions}
                                                    />
                                                )}
                                                <ListenedConditionController
                                                    listenedConditionOnForm={fieldRuleOnForm?.listenedConditionOnForm}
                                                >
                                                    <CustomField
                                                        {...(type === customFieldTypeMap.CHECKBOX
                                                            ? {
                                                                  checkboxDefaultValue: defaultValues['fields'][
                                                                      generateReactHookFormFriendlyFieldNameWithoutFields(
                                                                          key
                                                                      )
                                                                  ] as string
                                                              }
                                                            : {})}
                                                        containerProps={{
                                                            errorMessage: error?.message,
                                                            fieldRequirement:
                                                                fieldRuleOnForm?.required || fieldDefinition?.required
                                                        }}
                                                        disabled={isTicketClosed}
                                                        fieldDefinition={fieldDefinition}
                                                        value={value}
                                                        onBlur={onBlur}
                                                        onChange={onChange}
                                                        antdProps={{ ref }}
                                                    />
                                                </ListenedConditionController>
                                            </>
                                        );
                                    }}
                                    rules={{
                                        validate: getValidationRules({
                                            fieldRuleOnForm,
                                            fieldDefinition
                                        })
                                    }}
                                />
                            </ConditionApplier>
                        );
                    })}
                </Space>
            </Card>
            {!!gsocialFields.length && <Collapse items={items} />}
        </div>
    );
}

export {
    ConditionController,
    FORM_FIELD_KEY,
    ListenedConditionController,
    MIN_FIELD_ARRAY_LENGTH,
    generateReactHookFormFriendlyFieldName,
    generateReactHookFormFriendlyFieldNameWithoutFields,
    shouldRenderForOptimisticUI
};
